// File: src/contexts/DateContext.tsx
import React, { createContext, useState, useContext, ReactNode, useMemo } from 'react';
import { getTodayYYYYMMDD, parseDateFromYYYYMMDD, formatDateToYYYYMMDD } from '../utils/dateUtils';

interface DateContextType {
  selectedDate: Date;
  selectedDateString: string;
  setSelectedDate: (date: Date) => void;
}

const DateContext = createContext<DateContextType | undefined>(undefined);

export const DateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedDateState, setSelectedDateState] = useState<Date>(
    parseDateFromYYYYMMDD(getTodayYYYYMMDD()) // Initialize with today's date
  );

  // Memoize the string version of the date to prevent unnecessary re-renders of consumers
  const selectedDateString = useMemo(() => formatDateToYYYYMMDD(selectedDateState), [selectedDateState]);

  const setSelectedDate = (date: Date) => {
    setSelectedDateState(date);
  };

  return (
    <DateContext.Provider value={{ selectedDate: selectedDateState, selectedDateString, setSelectedDate }}>
      {children}
    </DateContext.Provider>
  );
};

export const useDate = (): DateContextType => {
  const context = useContext(DateContext);
  if (!context) {
    throw new Error('useDate must be used within a DateProvider');
  }
  return context;
};